import { MetricsState } from "@/types/metrics";

// Category weightings based on the US News methodology
const CATEGORY_WEIGHTS = {
  outcomes: 52,      // % weight of the final score
  expertOpinion: 20,
  facultyResources: 11,
  financialResources: 8,
  studentExcellence: 5,
  facultyResearch: 4,
};

// Sub-weights for the "Outcomes" category
const OUTCOMES_SUBWEIGHTS = {
  grad_rate: 16,        // 6-year graduation rate
  retention_rate: 5,
  pell_grad_rate: 5.5,
  non_pell_grad_rate: 5.5,
  overperformance_underperformance: 10,
  median_debt: 5,
  grads_earning_more: 5,
};

// Sub-weights for "Faculty Resources"
const FACRES_SUBWEIGHTS = {
  faculty_salaries_rank: 6,
  student_faculty_ratio: 3,
  percent_full_time_faculty_2: 2,
};

// Normalize values for each metric
const NORMALIZATION: Record<string, { min: number; max: number; invert?: boolean }> = {
  grad_rate: { min: 20, max: 98 },
  retention_rate: { min: 46, max: 99 },
  pell_grad_rate: { min: 13, max: 97 },
  non_pell_grad_rate: { min: 16, max: 98 },
  overperformance_underperformance: { min: -22, max: 21 },
  median_debt: { min: 5500, max: 31000, invert: true },
  grads_earning_more: { min: 46, max: 98 },
  peer_assessment: { min: 1.6, max: 4.8 },
  faculty_salaries_rank: { min: 1, max: 432, invert: true },
  student_faculty_ratio: { min: 3, max: 31, invert: true },
  percent_full_time_faculty_2: { min: 10, max: 100 },
  financial_resources_rank: { min: 1, max: 432, invert: true },
  sat_act_25th_75th: { min: 1, max: 36 },
  bibliometric_rank: { min: 1, max: 432, invert: true },
};

// Normalize the metric value for each metric
function normalizeMetricValue(metric: string, value: number): number {
  const info = NORMALIZATION[metric];
  if (!info) return 0;
  const { min, max, invert } = info;
  if (max <= min) return 0;
  let ratio = invert ? (max - value) / (max - min) : (value - min) / (max - min);
  return Math.max(0, Math.min(1, ratio));
}

// Extract the metric value from the provided state
function getMetricValue(metrics: MetricsState, dbField: string): number {
  for (const cat of Object.values(metrics)) {
    const item = cat.find(m => m.dbField === dbField);
    if (item) return item.value;
  }
  return 0;
}

export interface CategoryContribution {
  label: string;
  contribution: number;
  totalWeight: number;
}

// This hook returns the fixed category weights based on the official U.S. News methodology
export function useCategoryContributions(): CategoryContribution[] {
  return [
    {
      label: "Outcomes",
      contribution: 52,
      totalWeight: 52,
    },
    {
      label: "Expert Opinion",
      contribution: 20,
      totalWeight: 20,
    },
    {
      label: "Faculty Resources",
      contribution: 11,
      totalWeight: 11,
    },
    {
      label: "Financial Resources",
      contribution: 8,
      totalWeight: 8,
    },
    {
      label: "Student Excellence",
      contribution: 5,
      totalWeight: 5,
    },
    {
      label: "Faculty Research",
      contribution: 4,
      totalWeight: 4,
    },
  ];
}